import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Hub } from 'aws-amplify';
import { refreshAuthenticatedUser } from '../store/thunk/auth';
import { setAuthenticatedUser } from '../store/slices/auth';
import { EMPTY_AUTH } from '../store/types/auth';
import { isNotAuthorizedError } from '../api/auth';
import { triggerLogout } from '../api/client';

/** When the tab comes back to the foreground after at least this long, the session is re-read. */
export const INACTIVITY_REFRESH_MS = 15 * 60 * 1000;
/** Periodic re-check while the tab stays open and visible. */
export const PERIODIC_REFRESH_MS = 30 * 60 * 1000;

/**
 * Keeps `state.auth` in step with Cognito for long-lived tabs: group changes are picked up
 * without a re-login, and a revoked refresh token signs the user out instead of leaving a
 * dead session on screen. Renders nothing.
 */
const SessionKeeper = () => {
    const dispatch = useDispatch();

    useEffect(() => {
        let lastCheck = Date.now();
        const refresh = () => {
            lastCheck = Date.now();
            dispatch<any>(refreshAuthenticatedUser());
        };
        const onBack = () => {
            if (document.visibilityState !== 'visible') return;
            if (Date.now() - lastCheck >= INACTIVITY_REFRESH_MS) refresh();
        };
        const timer = window.setInterval(() => {
            // Skip the tick when a focus-triggered refresh happened a moment ago.
            if (document.visibilityState === 'visible' && Date.now() - lastCheck >= PERIODIC_REFRESH_MS - 60_000) refresh();
        }, PERIODIC_REFRESH_MS);
        document.addEventListener('visibilitychange', onBack);
        window.addEventListener('focus', onBack);
        const unsubscribe = Hub.listen('auth', ({ payload }) => {
            // Amplify reports a failed background refresh here; only a rejected refresh token
            // (revoked / expired) ends the session, a flaky network does not.
            if (payload.event === 'tokenRefresh_failure' && isNotAuthorizedError(payload.data)) triggerLogout();
            // Amplify signed the user out itself (disabled user, session cleanup): mirror it in the store.
            if (payload.event === 'signOut') dispatch((setAuthenticatedUser as any)(EMPTY_AUTH));
        });
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onBack);
            window.removeEventListener('focus', onBack);
            unsubscribe();
        };
    }, [dispatch]);

    return null;
};

export default SessionKeeper;
